import { Check, X } from 'lucide-react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './toast';
import { useToast } from './use-toast';
import { cn } from './utils';

/**
 * Toaster — pill-style snackbar.
 *
 * Layout per row:
 *   [icon] [title (+ description below if present)] [close on hover]
 *
 * - `success` → leading Check inside a state-success disc
 * - `destructive` → leading X inside a state-danger disc
 * - `default` → no icon (the title carries the message)
 *
 * The pill becomes a rounded panel (rounded-3xl) when a description is
 * present, otherwise stays a true pill (rounded-full). Close button is
 * hidden until hover/focus — auto-dismiss is the canonical exit.
 */
export function Toaster(): JSX.Element {
  const { toasts } = useToast();
  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        const multiline = Boolean(description);
        return (
          <Toast key={id} variant={variant} data-multiline={multiline} {...props}>
            <ToastIcon variant={variant} />
            <div className="flex min-w-0 flex-col">
              {title ? <ToastTitle>{title}</ToastTitle> : null}
              {description ? <ToastDescription>{description}</ToastDescription> : null}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

function ToastIcon({
  variant,
}: {
  variant: 'default' | 'success' | 'destructive' | null | undefined;
}): JSX.Element | null {
  if (variant !== 'success' && variant !== 'destructive') return null;
  const isSuccess = variant === 'success';
  return (
    <span
      className={cn(
        'flex h-6 w-6 flex-none items-center justify-center rounded-full',
        isSuccess
          ? 'bg-state-success text-state-success-foreground'
          : 'bg-state-danger text-state-danger-foreground'
      )}
      aria-hidden
    >
      {isSuccess ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
    </span>
  );
}
